import {
  BarChart2,
  Captions,
  FileText,
  Image as ImageIcon,
  Info,
  Mic,
  ShieldCheck,
  Trophy,
} from 'lucide-react'
import { useState } from 'react'
import type { ProjectTab } from '@/app/routes/_search'
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from '@/components/ui/Tabs'
import { EmptyState } from '@/components/ds/EmptyState'
import { VideoPlayer } from '@/components/media/VideoPlayer'
import { useVideo, type VideoItem } from '@/api/endpoints/videos'
import { videoStreamUrl, thumbnailUrl } from '@/api/endpoints/media'
import { useRescanSafety } from '@/api/endpoints/safety'
import { cn } from '@/lib/cn'
import { formatNumber, formatRelativeDate, formatScheduledLabel, formatScheduledTooltip, formatUSD } from '@/lib/format'
import type { VideoStatus } from '@/components/ds/StatusChip'
import { ProjectActions } from '@/features/projects/components/ProjectActions'
import { ProjectHeader } from '@/features/projects/components/ProjectHeader'
import { ProjectInfoGrid } from '@/features/projects/components/ProjectInfoGrid'
import { YoutubeDock } from '@/features/projects/components/YoutubeDock'
import { CaptionsDock } from '@/features/projects/components/CaptionsDock'
import { NarrationDock } from '@/features/projects/components/NarrationDock'
import { RankingDock } from '@/features/projects/components/RankingDock'
import { SafetyDock } from '@/features/projects/components/SafetyDock'
import { ScriptDock } from '@/features/projects/components/ScriptDock'
import { ThumbDock } from '@/features/projects/components/ThumbDock'
import { UploadModal } from '@/features/uploads/components/UploadModal'
import styles from './ProjectDetailPage.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

/** Names the project tabs: the last one picked (read by the route) and their dragged order. */
export const PROJECT_TABS_KEY = 'project-detail'

export interface ProjectDetailPageProps {
  projectId: string
  lang: string
  /** Active tab — URL-controlled by the route (``?tab=``, default ``info``). */
  tab: ProjectTab
  onTabChange: (next: ProjectTab) => void
}

const FACTORY_LABEL: Record<string, string> = {
  curiosity: 'Viral · curiosidade',
  product_review: 'Review · produto',
  football: 'Futebol · rodada',
  nasa_apod: 'APOD · astronomia',
  atlas: 'Atlas · mapa animado',
  mirage: 'Mirage · cena IA',
  pov_lives: 'POV Lives · vida narrada',
  quiz: 'Quiz · perguntas',
  reddit_stories: 'Reddit Stories · storytime narrado',
  etymology: 'Etimologia · origem das palavras',
  ranked: 'Ranked · Top N countdown',
  code_levels: 'Code Levels · junior vs senior',
  cortes: 'Cortes · Shorts de podcast',
}

// Factories that produce horizontal 16:9 video. The shorts grid (340px
// fixed column for the player) makes a 16:9 video read at ~191px tall,
// which is unwatchable. Landscape kinds collapse to a single column so
// the player gets the full row width. The VideoPlayer itself reads the
// real ratio from <video> metadata, so other formats added later get
// auto-corrected without an entry here — this is just the layout hint.
const LANDSCAPE_KINDS: ReadonlySet<string> = new Set(['pov_lives'])

export function ProjectDetailPage({ projectId, lang, tab, onTabChange }: ProjectDetailPageProps) {
  const { data: video, isPending, isError, error, refetch } = useVideo({ videoId: projectId, lang })
  const [uploadOpen, setUploadOpen] = useState(false)
  const rescanMutation = useRescanSafety()

  if (isError) {
    return (
      <QueryErrorNotice
        error={error}
        label="este projeto"
        onRetry={() => {
          void refetch()
        }}
      />
    )
  }
  if (isPending) {
    return <SkeletonShell />
  }
  // TanStack Query narrows `video` to VideoItem here because both
  // isError and isPending are false.

  const title = video.metadata?.title || video.meta_title || video.id
  const factoryLabel = FACTORY_LABEL[video.kind] ?? video.kind
  const src = videoStreamUrl(video.video_path)
  const poster = thumbnailUrl(video.thumbnail_path)

  return (
    <div className={styles.page}>
      <ProjectHeader
        eyebrow={`Projeto #${video.id.toUpperCase()} · ${statusToText(video.ui_status)}`}
        title={title}
        status={video.ui_status}
        factoryLabel={factoryLabel}
        language={video.language}
        onPublish={() => {
          setUploadOpen(true)
        }}
        safetyStatus={video.safety?.status ?? 'never_checked'}
        onRescanSafety={() => {
          rescanMutation.mutate({ videoId: video.id, lang: video.language })
        }}
        rescanRunning={rescanMutation.isPending}
        scheduledLabel={formatScheduledLabel(video.upload?.scheduled_for)}
        scheduledTooltip={formatScheduledTooltip(video.upload?.scheduled_for)}
        moreSlot={<ProjectActions video={video} mp4Url={src} />}
      />

      <UploadModal
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false)
        }}
        videoId={video.id}
        language={video.language}
        channel={video.channel}
        defaultTitle={video.metadata?.title ?? video.meta_title ?? ''}
        defaultDescription={video.metadata?.description ?? video.meta_description ?? ''}
        defaultTags={video.metadata?.hashtags ?? []}
      />

      <section
        className={cn(
          styles.playerWrap,
          LANDSCAPE_KINDS.has(video.kind) && styles['playerWrap--landscape'],
        )}
      >
        <VideoPlayer src={src} poster={poster} />
        {/* Where the info cards sat now holds every tab: "Informações" first,
            then what used to be the right-hand sidebar. */}
        <TabsRoot
          persistKey={PROJECT_TABS_KEY}
          value={tab}
          onValueChange={(value) => {
            onTabChange(value as ProjectTab)
          }}
          className={styles.tabs}
        >
          <TabsList aria-label="Seções do projeto">
            <TabsTrigger value="info" leadingIcon={<Info />}>Informações</TabsTrigger>
            <TabsTrigger value="ranking" leadingIcon={<Trophy />}>Ranking</TabsTrigger>
            <TabsTrigger value="script" leadingIcon={<FileText />}>Roteiro</TabsTrigger>
            <TabsTrigger value="narration" leadingIcon={<Mic />}>Narração</TabsTrigger>
            <TabsTrigger value="captions" leadingIcon={<Captions />}>Legendas</TabsTrigger>
            <TabsTrigger value="thumb" leadingIcon={<ImageIcon />}>Thumb</TabsTrigger>
            <TabsTrigger value="safety" leadingIcon={<ShieldCheck />}>Safety</TabsTrigger>
            <TabsTrigger value="youtube" leadingIcon={<BarChart2 />}>YouTube</TabsTrigger>
          </TabsList>
          <TabsContent value="info" className={styles.tabBody}>
            <ProjectInfoGrid items={buildInfoItems(video)} />
          </TabsContent>
          <TabsContent value="ranking" className={styles.tabBody}>
            <RankingDock videoId={video.id} lang={video.language} />
          </TabsContent>
          <TabsContent value="script" className={styles.tabBody}>
            {video.script ? (
              <ScriptDock
                hook={extractHook(video.script)}
                segments={extractSegments(video.script)}
                closing={extractClosing(video.script)}
              />
            ) : (
              <EmptyState title="Sem roteiro" description="Este vídeo não tem roteiro salvo." />
            )}
          </TabsContent>
          <TabsContent value="narration" className={styles.tabBody}>
            <NarrationDock
              audioPath={video.audio_path}
              durationSec={video.duration_sec ?? null}
              language={video.language}
            />
          </TabsContent>
          <TabsContent value="captions" className={styles.tabBody}>
            <CaptionsDock script={video.script} durationSec={video.duration_sec ?? null} />
          </TabsContent>
          <TabsContent value="thumb" className={styles.tabBody}>
            <ThumbDock
              videoId={video.id}
              language={video.language}
              channel={video.channel}
              thumbnailPath={video.thumbnail_path || null}
              defaultText={video.metadata?.thumbnail_text ?? ''}
            />
          </TabsContent>
          <TabsContent value="safety" className={styles.tabBody}>
            <SafetyDock videoId={video.id} lang={video.language} />
          </TabsContent>
          <TabsContent value="youtube" className={styles.tabBody}>
            <YoutubeDock videoId={video.id} lang={video.language} title={title} />
          </TabsContent>
        </TabsRoot>
      </section>
    </div>
  )
}

function SkeletonShell() {
  return (
    <div className={styles.skeletonShell} aria-busy>
      <div className={styles.skeletonHeader} />
      <div className={styles.skeletonPlayerRow}>
        <div className={styles.skeletonPlayer} />
        <div className={styles.skeletonStage} />
      </div>
    </div>
  )
}

function statusToText(s: VideoStatus): string {
  switch (s) {
    case 'publicado':
      return 'publicado'
    case 'agendado':
      return 'agendado'
    case 'gerando':
      return 'gerando'
    case 'falhou':
      return 'falhou'
    case 'rascunho':
    default:
      return 'rascunho'
  }
}

function buildInfoItems(v: VideoItem) {
  const items: { label: string; value: React.ReactNode }[] = []
  // Backend doesn't expose the actual video dimensions yet, so we
  // reverse-derive from the factory kind. ``LANDSCAPE_KINDS`` is the
  // single source of truth and stays in sync with the layout grid.
  // TODO: surface real width/height in the videos API (factory_meta)
  // and read it here instead.
  const resolution = LANDSCAPE_KINDS.has(v.kind) ? '1920×1080' : '1080×1920'
  items.push({ label: 'Resolução', value: resolution })
  if (v.created_at) {
    items.push({ label: 'Criado em', value: formatRelativeDate(v.created_at) })
  }
  if (v.upload?.uploaded_at) {
    items.push({ label: 'Publicado em', value: new Date(v.upload.uploaded_at).toLocaleDateString('pt-BR') })
  }
  if (v.upload?.youtube_video_id) {
    items.push({ label: 'YouTube', value: v.upload.youtube_video_id })
  }
  const cost = readCostSummary(v)
  if (cost) {
    const sub = [cost.calls ? `${formatNumber(cost.calls)} calls` : null, cost.model || null]
      .filter(Boolean)
      .join(' · ')
    items.push({
      label: 'Custo (LLM)',
      value: (
        <span title={sub || undefined}>
          {formatUSD(cost.totalUsd)}
          {sub ? <span style={{ display: 'block', fontSize: '0.75em', opacity: 0.6 }}>{sub}</span> : null}
        </span>
      ),
    })
  }
  return items
}

interface CostSummaryView {
  totalUsd: number
  calls: number
  model: string
}

/** Pull cost_summary off the opaque VideoItem extras. Returns null when
 *  the field is missing or zero — projects generated before cost tracking
 *  shouldn't render an empty row. */
function readCostSummary(v: VideoItem): CostSummaryView | null {
  const raw = (v as unknown as Record<string, unknown>)['cost_summary']
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const totalUsd = typeof o['total_usd'] === 'number' ? o['total_usd'] : 0
  if (totalUsd <= 0) return null
  return {
    totalUsd,
    calls: typeof o['calls'] === 'number' ? o['calls'] : 0,
    model: typeof o['model'] === 'string' ? o['model'] : '',
  }
}

function extractHook(script: unknown): string {
  if (!script || typeof script !== 'object') return ''
  const s = script as Record<string, unknown>
  return typeof s['hook'] === 'string' ? s['hook'] : ''
}

function extractClosing(script: unknown): string {
  if (!script || typeof script !== 'object') return ''
  const s = script as Record<string, unknown>
  return typeof s['closing'] === 'string' ? s['closing'] : ''
}

function extractSegments(script: unknown) {
  if (!script || typeof script !== 'object') return []
  const s = script as Record<string, unknown>
  const raw = s['segments']
  if (!Array.isArray(raw)) return []
  return raw.map((seg) => {
    const o = (seg ?? {}) as Record<string, unknown>
    return {
      text: typeof o['text'] === 'string' ? o['text'] : '',
      duration_hint:
        typeof o['duration_hint'] === 'number' ? o['duration_hint'] : null,
    }
  })
}
